import React, { Fragment, useEffect, useState } from "react"
import moment from "moment"
import { CampingStyleProps, CampProps, ReservationProps, SiteProps } from "../interfaces"
import { formatPhoneNumber, titleCase } from "../utils"
import {
  ArrowUturnLeftIcon,
  BoltIcon,
  ChevronUpIcon,
  MapIcon,
  MapPinIcon,
  ArrowUturnRightIcon,
  EyeIcon,
  CreditCardIcon,
  ArrowRightEndOnRectangleIcon,
  DocumentIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/solid"
import { MapPinIcon as MapPinIconTiny } from "@heroicons/react/16/solid"
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch"
import { isMobile } from "react-device-detect"
import { ELECTRICS, KINDS, PAYMENT_MODES, RESERVATION_KINDS, STATUSES } from "../constants"
import createMarkup from "../utils/createMarkup"
import { DateTime } from "luxon"
import { Transition } from "@headlessui/react"
import left_right_icon from "../../assets/images/arrow_icons/left_right_arrows.svg"
import { validKinds } from "../types"
import copyText from "../utils/copyText"
import copy from "../../assets/images/copy_icons/copy.svg"
import copied from "../../assets/images/copy_icons/copied.svg"
import CopyIcon from "../../assets/images/copy_icons/copy.svg?react"
import CopiedIcon from "../../assets/images/copy_icons/copied.svg?react"
import Button from "./ui/Button"
import IInvoice from "../interfaces/IInvoice"
import api2 from "../api2"
import Spinner from "./ui/Spinner"
import classNames from "../utils/classNames"
import InvoiceDecorator from "../decorators/InvoiceDecorator"
import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/20/solid"
import formatDateRange from "~/utils/admin/formatDateRange"
import RealTimeIndicator from "./Reservations/RealTimeIndicator"
import TermsAgreementModal from "./TermsAgreement/TermsAgreementModal"
import { formatMoney, formatMoneyFromCents } from "~/utils/formatMoney"
import parseDateTime from "~/utils/parseDateTime"
import formatCampingStyleName from "~/utils/formatCampingStyleName"
import { StatusBlocked, StatusDeleted, StatusImported, StatusPaid, StatusUnpaid } from "./ui/StatusBadges"

interface ComponentProps {
  reservation: ReservationProps
  email: string
  site: SiteProps
  camp: CampProps
  is_admin: boolean
  camping_style: CampingStyleProps
  invoice: IInvoice[]
}

const formatElectricValue = (value: string) => {
  if (!Number.isNaN(Number(value))) {
    return value + "A"
  }

  const splittedValue = value.split("/")

  if (splittedValue?.length > 1) {
    let formattedValue = ""
    splittedValue.forEach((value, i) => (formattedValue += (i !== 0 ? "/" : "") + value + "A"))
    return formattedValue
  }

  if (!value) return ELECTRICS.NONE[0].toUpperCase() + ELECTRICS.NONE.slice(1, ELECTRICS.NONE.length)

  return value[0].toUpperCase() + value.slice(1, value.length)
}

const RvKindImage = ({ kind }: { kind: validKinds }) => {
  if (kind === KINDS.RV_BACKIN) {
    return <ArrowUturnLeftIcon className="w-4" />
  }

  if (kind === KINDS.RV_PULLIN) {
    return <ArrowUturnRightIcon className="w-4" />
  }

  return <MapPinIconTiny className="w-4 mb-0.5" />
}

type panelNames = "reservation" | "todo" | "invoices" | "rules" | "map"

const Confirmation = (props: ComponentProps) => {
  const todos: any[] = []

  const [openedPanel, setOpenedPanel] = useState<panelNames>("reservation")
  const [invoice, setInvoice] = useState<IInvoice>()
  const [reservation, setReservation] = useState<ReservationProps>(props.reservation)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [taskCount, setTaskCount] = useState(0)
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState(!!props.reservation.tos_agreed_at)
  const isMapPresent = props.camping_style.map_url || props.camp.map_url

  const startDate = parseDateTime(reservation.start_date, props.camp.timezone, { asMoment: true })
  const endDate = parseDateTime(reservation.end_date, props.camp.timezone, { asMoment: true })

  const openGoogleMaps = () => {
    const address = encodeURIComponent(
      `${props.camp.country} ${props.camp.state} ${props.camp.city} ${props.camp.address}`,
    )
    const url = `https://www.google.com/maps/search/?api=1&query=${address}`
    window.open(url, "_blank")
  }

  const handleCheckin = () => {
    setIsLoading(true)
    api2.Reservations.checkIn(reservation.id)
      .then((res) => {
        setReservation(res.reservation)
        setIsLoading(false)
        if (taskCount > 0) setTaskCount(taskCount - 1)
      })
      .catch((e) => {
        console.error(e)
        setIsLoading(false)
      })
  }

  const handleAcceptAndSign = (tos_signature: string) => {
    setIsLoading(true)

    api2.Reservations.signTerms(reservation.id as string, {
      tosSignature: tos_signature,
      tosAgreedAt: DateTime.now().setZone(props.camp.timezone).toISO() as string,
      lockVersion: reservation.lock_version,
    })
      .then((response) => {
        setReservation({
          ...response.reservation,
          tos_signature,
          tos_agreed_at: DateTime.now().setZone(props.camp.timezone).toISO() as string,
        })
        setIsTermsAgreed(true)
        setIsLoading(false)
        if (taskCount > 0) setTaskCount(taskCount - 1)
      })
      .catch((e) => {
        console.log(e)
        setIsLoading(false)
      })
  }

  const handleTermsAgreement = () => {
    setIsTermsModalOpen(true)
  }

  useEffect(() => {
    if (reservation?.order_id) {
      api2.Invoices.fetchForOrder(reservation?.order_id)
        .then(({ invoice }) => {
          setInvoice(invoice)
        })
        .catch((e) => console.log(e))
    }
  }, [])

  return (
    <>
      {isLoading && (
        <div className="fixed h-full w-full inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
          <Spinner size={12} />
        </div>
      )}
      <div
        className="flex justify-center h-30 shadow-none drop-shadow-none"
        style={{ backgroundColor: props.camp.primary_color }}
      >
        <div className="ml-4 flex-grow flex items-center my-8">
          <div id="camp-text" style={{ color: props.camp.header_color }}>
            <h2 className="text-2xl sm:text-3xl header pt-0.5 font-semibold" style={{ fontFamily: "Lora" }}>
              {props.camp.name}
            </h2>
            <span className="flex items-center mb-1.5 mt-1">
              <MapPinIcon className="w-5 h-5 mr-1.5" />
              <span>
                {props.camp.city && !props.camp.state
                  ? `${props.camp.city}`
                  : `${props.camp.city}, ${props.camp.state}`}
              </span>
            </span>
          </div>
          {props.camp.logo_url && (
            <img src={props.camp.logo_url} className="h-32 ml-auto py-2 mr-4 " style={{ maxWidth: "50%" }} />
          )}
        </div>
      </div>
      <div className="relative flex justify-center md:justify-start" style={{ height: "calc(100vh - 105px)" }}>
        {isMapPresent && (
          <div className="hidden xsm:flex min-w-1/2 max-w-[70%] justify-center pt-2.5 px-1 px-5">
            <TransformWrapper>
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",

                  height: "100%",
                }}
              >
                <img
                  src={props.camping_style.map_url || props.camp.map_url}
                  alt="No map available."
                  style={{
                    maxHeight: "calc(90vh - 66px)",

                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </TransformComponent>
            </TransformWrapper>
          </div>
        )}
        <div
          className={classNames(
            !openedPanel && "md:opacity-80",
            "transition-opacity absolute bg-white xsm:inset-x-4 xsm:left-auto xsm:right-6 xsm:top-6 rounded-3xl xsm:shadow-md",
            "max-w-auto xsm:max-w-lg lg:max-w-2xl w-full px-4 py-3 flex flex-col max-h-[calc(100vh-140px)]",
          )}
        >
          <ReservationSection
            setOpenedPanel={setOpenedPanel}
            openedPanel={openedPanel}
            startDate={startDate}
            endDate={endDate}
            camp={props.camp}
            openGoogleMaps={openGoogleMaps}
            site={props.site}
            camping_style={props.camping_style}
            reservation={reservation}
          />
          <TodoSection
            setOpenedPanel={setOpenedPanel}
            openedPanel={openedPanel}
            todos={todos}
            invoice={props.invoice}
            reservation={reservation}
            handleCheckin={handleCheckin}
            handleTermsAgreement={handleTermsAgreement}
            taskCount={taskCount}
            setTaskCount={setTaskCount}
            isTermsAgreed={isTermsAgreed}
            camp={props.camp}
            camping_style={props.camping_style}
          />
          <RulesSection setOpenedPanel={setOpenedPanel} openedPanel={openedPanel} camp={props.camp} />
          <div className="inline xsm:hidden">
            <MapSection
              setOpenedPanel={setOpenedPanel}
              openedPanel={openedPanel}
              mapUrl={props.camping_style.map_url || props.camp.map_url}
            />
          </div>
        </div>
      </div>
      <Transition
        show={isTermsModalOpen}
        enter="transition-opacity duration-700"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <TermsAgreementModal
          open={isTermsModalOpen}
          onClose={() => {
            setIsTermsModalOpen(false)
          }}
          campName={props.camp.name}
          rules={props.camp.rules}
          legalAcknowledgements={props.camping_style.legal_acknowledgements}
          isTermsAgreed={isTermsAgreed}
          setIsTermsAgreed={setIsTermsAgreed}
          tos_signature={reservation.tos_signature}
          tos_agreed_at={reservation.tos_agreed_at}
          handleAcceptAndSign={handleAcceptAndSign}
          reservation={reservation}
          camp={props.camp}
        />
      </Transition>
    </>
  )
}

const ReservationSection = ({
  setOpenedPanel,
  openedPanel,
  startDate,
  endDate,
  camp,
  openGoogleMaps,
  site,
  camping_style,
  reservation,
}) => {
  const isPaidReservation = reservation.status === STATUSES.PAID || reservation.payment_mode == PAYMENT_MODES.ONLINE
  return (
    <div
      className={classNames(
        "flex flex-col",
        openedPanel === "reservation" && "overflow-y-auto border-b border-gray-200",
      )}
    >
      <Button
        variant="none"
        className="py-4 w-full flex justify-between px-0 items-center"
        style={{ paddingLeft: 0, paddingRight: 0 }}
        onClick={() => setOpenedPanel(openedPanel === "reservation" ? null : "reservation")}
      >
        <span className="font-bold text-lg">Reservation summary</span>
        <ChevronUpIcon className={`${openedPanel !== "reservation" ? "rotate-180 transform" : ""} h-5 w-5 `} />
      </Button>
      {openedPanel === "reservation" && (
        <div className="flex flex-0 max-h-full flex-col gap-4.5 overflow-y-auto pr-2">
          <div className="text-gray-500">
            For your reservation on {startDate.format(moment().year() === startDate.year() ? "MMM Do" : "MMM Do, YYYY")}
          </div>
          <CheckDates checkinTime={startDate} checkoutTime={endDate} />
          <div className="flex items-center justify-between">
            <div className="w-full">
              <div className="font-semibold mb-1.5">Location</div>
              <div className="font-normal text-base text-gray-500 pr-1 truncate">{camp.address}</div>
            </div>
            <div
              className="p-2 hover:bg-gray-150 w-10 h-10 text-gray-500 rounded-md cursor-pointer"
              style={{ transition: "all .3s" }}
              onClick={openGoogleMaps}
            >
              <MapIcon />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="w-full">
              <div className="font-semibold mb-1.5">Contact the property</div>
              {camp.phone && (
                <div className="font-normal text-base text-gray-500 pr-1 truncate">{formatPhoneNumber(camp.phone)}</div>
              )}
              {camp.email && <div className="font-normal text-base text-gray-500 pr-1 truncate">{camp.email}</div>}
            </div>
            <div className="flex items-center gap-x-1">
              {camp.phone && (
                <a
                  className="p-2 hover:bg-gray-150 w-10 h-10 text-gray-500 rounded-md cursor-pointer"
                  style={{ transition: "all .3s" }}
                  href={`tel:${camp.phone}`}
                >
                  <PhoneIcon />
                </a>
              )}
              {camp.email && (
                <a
                  className="p-2 hover:bg-gray-150 w-10 h-10 text-gray-500 rounded-md cursor-pointer"
                  style={{ transition: "all .3s" }}
                  href={`mailto:${camp.email}?subject=Question regarding ${formatDateRange(
                    startDate,
                    endDate,
                  )} reservation`}
                >
                  <EnvelopeIcon />
                </a>
              )}
            </div>
          </div>
          {camping_style.is_showing_site_in_notifiers && <div className="">
            <div className="font-semibold mb-1.5">Site</div>
            <div className="text-gray-500 font-normal">{titleCase(site.name)}</div>
            <div className="flex gap-4 items-center w-full">
              {typeof site.max_rig_length === "number" && site.max_rig_length && (
                <div className="flex items-center gap-1">
                  <div className="text-gray-500">{site.max_rig_length} ft max</div>
                  <img src={left_right_icon} alt="max_length_icon" />
                </div>
              )}
              {site.electric && (
                <div className="flex gap-1">
                  <span className="text-gray-500">{formatElectricValue(site.electric)}</span>
                  <BoltIcon className="text-yellow-300 w-4" />
                </div>
              )}
              <div className="flex gap-1">
                <div className="text-gray-500">{formatCampingStyleName(site.kind)}</div>
                <RvKindImage kind={site.kind} />
              </div>
            </div>
          </div>}
          {camping_style.checkin_policy?.length || site.checkin_policy_additions?.length ? (
            <div className="">
              <div className="font-semibold mb-1.5">Check-in policy</div>
              <div
                className="text-gray-500"
                dangerouslySetInnerHTML={createMarkup(
                  [camping_style.checkin_policy || "", site.checkin_policy_additions || ""].join("\n"),
                )}
              />
            </div>
          ) : (
            <> </>
          )}
          {camping_style.refund_policy ? (
            <div className="">
              <div className="font-semibold mb-1.5">Cancellation policy</div>
              <div className="text-gray-500" dangerouslySetInnerHTML={createMarkup(camping_style.refund_policy)} />
            </div>
          ) : (
            <> </>
          )}
          {camp.wifi_network?.length ? (
            <div className="flex w-full justify-between items-center">
              <div className="">
                <div className="font-semibold mb-1.5">Wi-fi network name</div>
                <div className="text-gray-500 font-normal text-base">{camp.wifi_network}</div>
              </div>
              <CopyButton value={camp.wifi_network} />
            </div>
          ) : (
            <></>
          )}
          {camp.wifi_password?.length ? (
            <div className="flex w-full justify-between items-center">
              <div className="">
                <div className="font-semibold mb-1.5">Wi-fi password</div>
                <div className="text-gray-500 font-normal text-base">{camp.wifi_password}</div>
              </div>
              <CopyButton value={camp.wifi_password} />
            </div>
          ) : (
            <></>
          )}
          {camp.bathroom_code?.length ? (
            <div className="flex w-full justify-between items-center">
              <div className="">
                <div className="font-semibold mb-1.5">Bathroom code</div>
                <div className="text-gray-500 font-normal text-base">{camp.bathroom_code}</div>
              </div>
            </div>
          ) : (
            <></>
          )}
          {camp.laundry_code?.length ? (
            <div className="flex w-full justify-between items-center">
              <div className="">
                <div className="font-semibold mb-1.5">Laundry room code</div>
                <div className="text-gray-500 font-normal text-base">{camp.laundry_code}</div>
              </div>
              <CopyButton value={camp.laundry_code} />
            </div>
          ) : (
            <></>
          )}
          <div>
            <div className="font-semibold">Reservation Amount</div>
            <div className="flex w-full justify-between items-center">
              <div className="flex justify-between items-center w-full">
                <div className="flex items-center">
                  <div className="text-gray-500 font-normal text-base">
                    {formatMoneyFromCents(reservation.total_owed_in_cents, camp.currency)}
                  </div>
                  <div className="font-medium ml-2">
                    {reservation?.deleted_at ? (
                      <StatusDeleted deleted_at={moment(reservation?.deleted_at).fromNow()} />
                    ) : reservation?.status === STATUSES.UNPAID ? (
                      <StatusUnpaid />
                    ) : isPaidReservation ? (
                      <StatusPaid refundedAmountInCents={reservation?.order?.amount_refunded_in_cents} />
                    ) : [STATUSES.IMPORTED, "blocked-import"].includes(reservation?.status) ? (
                      <StatusImported />
                    ) : (
                      <StatusBlocked />
                    )}
                  </div>
                </div>
                <Button variant="none" style={{ padding: 0 }} className="ml-auto">
                  <a
                    href={`/i/${reservation?.hashed_id}`}
                    className="flex gap-2 border py-2 px-3 rounded items-center hover:text-black whitespace-nowrap hover:bg-gray-200 border-gray-200"
                    style={{ transition: "all .3s" }}
                  >
                    <DocumentTextIcon className="w-5 h-5 text-gray-500 hidden sm:block" />
                    {isPaidReservation ? "View receipt" : "View invoice"}
                  </a>
                </Button>
              </div>
            </div>
          </div>
          <div />
        </div>
      )}
    </div>
  )
}

interface TodoProps {
  setOpenedPanel: React.Dispatch<React.SetStateAction<panelNames>>
  openedPanel: panelNames
  todos: any[]
  invoice: InvoiceDecorator[]
  reservation: ReservationProps
  handleCheckin: () => void
  handleTermsAgreement: () => void
  taskCount: number
  setTaskCount: any
  isTermsAgreed: boolean
  camp: CampProps
  camping_style: CampingStyleProps
}

const TodoSection = ({
  setOpenedPanel,
  openedPanel,
  todos,
  invoice,
  reservation,
  handleCheckin,
  handleTermsAgreement,
  taskCount,
  setTaskCount,
  isTermsAgreed,
  camp,
  camping_style,
}: TodoProps) => {
  const isPaidReservation = reservation.status === STATUSES.PAID
  const isCheckedIn = reservation.is_checked_in
  const nowInCamp = DateTime.now().setZone(camp.timezone)
  const startDateInCamp = DateTime.fromISO(reservation.start_date, { zone: camp.timezone })

  const isTodayOrAfterStartDate = nowInCamp.startOf("day") >= startDateInCamp.startOf("day")

  const isAllowedSelfCheckIn = camping_style?.is_allowed_self_check_in && isTodayOrAfterStartDate
  const isPaymentToDoVisible = reservation.payment_mode !== PAYMENT_MODES.ONLINE 
  const isSignToDoVisible = reservation.payment_mode !== PAYMENT_MODES.ONLINE && camp.tos_requires_signature

  const isToDosVisible = isPaymentToDoVisible || isSignToDoVisible || isAllowedSelfCheckIn

  useEffect(() => {
    let count = 0

    if (reservation.payment_mode != PAYMENT_MODES.ONLINE && !isPaidReservation) {
      count++
    }

    if (!isCheckedIn && isAllowedSelfCheckIn) {
      count++
    }

    if (!isTermsAgreed && reservation.payment_mode != PAYMENT_MODES.ONLINE && camp.tos_requires_signature) {
      count++
    }

    setTaskCount(count)
  }, [])

  return (
    <div className={classNames("flex flex-col", openedPanel === "todo" && "overflow-y-auto")}>
      <Button
        variant="none"
        className="py-4 w-full flex justify-between px-0 items-center"
        style={{ paddingLeft: 0, paddingRight: 0 }}
        onClick={() => setOpenedPanel(openedPanel === "todo" ? null : "todo")}
      >
        <div className="flex justify-start">
          <span className="font-bold text-lg">To-do's </span>
          {taskCount > 0 && (
            <div className="flex items-center justify-center mb-4 ml-3.5">
              <span className={classNames("bg-red-500", "w-5 h-5 rounded-full absolute")}></span>
              <span className={classNames("bg-red-500", "w-[12px] h-[12px] rounded-full absolute animate-ping")}></span>
              <span className="text-white text-sm font-medium z-10 absolute">{taskCount}</span>
            </div>
          )}
        </div>
        <ChevronUpIcon className={`${openedPanel !== "todo" ? "rotate-180 transform" : ""} h-5 w-5 `} />
      </Button>
      {openedPanel === "todo" && (
        <>
          {isToDosVisible ? (
            <div className="flex flex-col gap-4.5 overflow-y-auto pr-2">
              {isPaymentToDoVisible && (
                <div className="py-3 px-3.5 xs:py-4 xs:px-4.5 border border-gray-200 flex justify-between items-center rounded-md">
                  <div className="flex gap-2.5 items-center">
                    {isPaidReservation && (
                      <input
                        checked={isPaidReservation}
                        type="checkbox"
                        className="focus-none text-white border border-gray-200 rounded mt-0.5 confirmationPageCheckedInput hidden xs:block"
                      />
                    )}
                    <div
                      className={classNames(
                        "flex flex-wrap gap-x-2 gap-y-1 items-center",
                        isPaidReservation ? "text-gray-300 line-through" : "",
                      )}
                    >
                      <div className="font-semibold text-md xs:text-base">Pay in advance</div>{" "}
                      {/* TODO: make this key off of order payment links, since this can show up once they've viewed the onnline payment portal */}
                      {!isPaidReservation && reservation.stripe_payment_link_url ? (
                        <div className="text-red-550 font-semibold bg-red-100 px-2.5 py-1 rounded-3xl text-xs">
                          Required
                        </div>
                      ) : (
                        <div className="text-gray-700 font-semibold bg-gray-200 px-2.5 py-1 rounded-3xl text-xs">
                          Optional
                        </div>
                      )}
                    </div>
                  </div>
                  <Button variant="none" disabled={isPaidReservation} style={{ padding: 0 }}>
                    <a
                      href={`/i/${reservation?.hashed_id}`}
                      className={classNames(
                        "flex gap-2 border py-2 px-3 rounded items-center hover:text-black whitespace-nowrap",
                        isPaidReservation ? " cursor-not-allowed" : "hover:bg-gray-200 border-gray-200",
                      )}
                      style={{ transition: "all .3s" }}
                    >
                      <CreditCardIcon className="w-5 h-5 text-gray-700 hidden sm:block" />
                      Pay now
                    </a>
                  </Button>
                </div>
              )}
              {isSignToDoVisible && (
                <div className="py-3 px-3.5 xs:py-4 xs:px-4.5 border border-gray-200 flex justify-between items-center rounded-md">
                  <div
                    className={classNames(
                      "flex flex-wrap gap-2.5 items-center",
                      isTermsAgreed ? "text-gray-300 line-through" : "",
                    )}
                  >
                    {isTermsAgreed && (
                      <input
                        checked={isTermsAgreed}
                        type="checkbox"
                        className="focus-none text-white border border-gray-200 rounded mt-0.5 confirmationPageCheckedInput hidden xs:block"
                      />
                    )}
                    <div className="font-semibold text-md xs:text-base">Read and sign Terms & Conditions</div>
                    {!isTermsAgreed && (
                      <div className="text-red-550 font-semibold bg-red-100 px-2.5 py-1 rounded-3xl text-xs">
                        Required
                      </div>
                    )}
                  </div>
                  <Button
                    variant="none"
                    className="whitespace-nowrap flex gap-2 bg-none border border-gray-200 py-2 px-3 rounded items-center hover:bg-gray-200 hover:text-black"
                    style={{ transition: "all .3s" }}
                    onClick={handleTermsAgreement}
                    disabled={isTermsAgreed}
                  >
                    <DocumentIcon className="w-5 h-5 hidden sm:block" />
                    Read & sign
                  </Button>
                </div>
              )}
              {isAllowedSelfCheckIn && (
                <div className="py-3 px-3.5 xs:py-4 xs:px-4.5 border border-gray-200 flex justify-between items-center rounded-md">
                  <div
                    className={classNames(
                      "flex flex-wrap gap-2.5 items-center",
                      isCheckedIn ? "text-gray-300 line-through" : "",
                    )}
                  >
                    {isCheckedIn && (
                      <input
                        checked={isCheckedIn}
                        type="checkbox"
                        className="focus-none text-white border border-gray-200 rounded mt-0.5 confirmationPageCheckedInput hidden xs:block"
                      />
                    )}
                    <div className="font-semibold text-md xs:text-base">Check-in upon arrival</div>
                  </div>
                  <Button
                    variant="none"
                    className="whitespace-nowrap flex gap-2 bg-none border border-gray-200 py-2 px-3 rounded items-center hover:bg-gray-200 hover:text-black"
                    style={{ transition: "all .3s" }}
                    onClick={handleCheckin}
                    disabled={isCheckedIn}
                  >
                    <ArrowRightEndOnRectangleIcon className="w-5 h-5 hidden sm:block" />
                    Check in
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4.5 overflow-y-auto pr-2">
              <div className="text-center text-gray-600 py-8">You don’t have any To-Do’s, enjoy your stay!</div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface RulesProps {
  setOpenedPanel: React.Dispatch<React.SetStateAction<panelNames>>
  openedPanel: panelNames
  camp: CampProps
}

const RulesSection = ({ setOpenedPanel, openedPanel, camp }: RulesProps) => {
  return (
    <div className={classNames("flex flex-col", openedPanel === "rules" && "overflow-y-auto")}>
      <Button
        variant="none"
        className="py-4 w-full flex justify-between px-0 items-center"
        style={{ paddingLeft: 0, paddingRight: 0 }}
        onClick={() => setOpenedPanel(openedPanel == "rules" ? null : "rules")}
      >
        <span className="font-bold text-lg">Terms & Conditions</span>
        <ChevronUpIcon className={`${openedPanel !== "invoices" ? "rotate-180 transform" : ""} h-5 w-5 `} />
      </Button>
      {openedPanel === "rules" && (
        <div className="flex flex-col gap-4.5 overflow-y-auto pr-2">
          {camp.rules ? (
            <div
              className={`${"rules-container html-editor-container"}`}
              dangerouslySetInnerHTML={createMarkup(camp.rules)}
            />
          ) : (
            <div></div>
          )}
        </div>
      )}
    </div>
  )
}

interface MapSection {
  setOpenedPanel: React.Dispatch<React.SetStateAction<panelNames>>
  openedPanel: panelNames
  mapUrl: string
}

const MapSection = ({ setOpenedPanel, openedPanel, mapUrl }: MapSection) => {
  return (
    <div className={classNames("flex flex-col mb-12")}>
      <Button
        variant="none"
        className="py-4 w-full flex justify-between px-0 items-center"
        style={{ paddingLeft: 0, paddingRight: 0 }}
        onClick={() => setOpenedPanel(openedPanel == "map" ? null : "map")}
      >
        <span className="font-bold text-lg">Map</span>
        <ChevronUpIcon className={`${openedPanel !== "map" ? "rotate-180 transform" : ""} h-5 w-5 `} />
      </Button>
      {openedPanel === "map" && <img src={mapUrl} alt="No map available." className="h-full" />}
    </div>
  )
}

const CopyButton = ({ value }: { value: string }) => {
  const [clicked, setClicked] = useState(false)

  const handleCopy = () => {
    copyText(value)
    setClicked(true)
  }

  useEffect(() => {
    if (clicked) {
      setTimeout(() => {
        setClicked(false)
      }, 3000)
    }
  }, [clicked])

  return (
    <div className="relative max-w-full">
      <Transition
        show={clicked}
        as={Fragment}
        enter="transform ease-out duration-300 transition"
        enterFrom="translate-y-2 opacity-0 sm:translate-y-2 sm:translate-x-0"
        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
        leave="transform ease-out duration-300 transition"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="translate-y-2 opacity-0"
      >
        <div
          className="absolute text-gray-600 right-0 bottom-full mb-2 border border-gray-150 flex bg-white py-1 px-2 items-center rounded-md whitespace-nowrap"
          style={{ boxShadow: "0px 2px 4px 0px #0000001A" }}
        >
          Copied to clipboard
        </div>
      </Transition>
      <button
        children={
          <div className="flex items-center justify-center">
            {clicked ? (
              <CopiedIcon className="h-6 w-6 text-gray-500 stroke-gray-500" />
            ) : (
              <CopyIcon className="h-6 w-6 text-gray-500 stroke-gray-500" />
            )}
          </div>
        }
        className="hover:bg-gray-100 text-gray-500 hover:text-gray-500 p-1.5 w-10 h-10 flex justify-center items-center rounded-md"
        onClick={handleCopy}
        style={{ transition: "all .3s" }}
      />
    </div>
  )
}

const CheckDates = ({ checkinTime, checkoutTime }: { checkinTime: moment.Moment; checkoutTime: moment.Moment }) => {
  return (
    <div className="w-full flex justify-between items-center">
      <div className="">
        <div className="font-semibold mb-1.5">Check-in</div>
        <div className="text-gray-500 font-normal">{checkinTime.format("ddd, MMM D")}</div>
        <div className="text-gray-500 font-normal">{checkinTime.format("h:mm A")}</div>
      </div>
      <div className="w-0.25 h-12 bg-gray-300"></div>
      <div className="">
        <div className="font-semibold mb-1.5">Check-out</div>
        <div className="text-gray-500 font-normal">{checkoutTime.format("ddd, MMM D")}</div>
        <div className="text-gray-500 font-normal">{checkoutTime.format("h:mm A")}</div>
      </div>
    </div>
  )
}

const InvoiceItem = ({ invoice, reservation }: { invoice: IInvoice; reservation: ReservationProps }) => {
  return (
    <div className="py-3 px-3.5 xs:py-4 xs:px-4.5 border border-gray-200 flex justify-between items-center rounded-md">
      <div className="font-normal text-md xs:text-base">
        {moment(reservation.start_date).format("MMM DD")}-{moment(reservation.end_date).format("MMM DD")}
      </div>
      <a
        href={`/i/${reservation.hashed_id}`}
        className="flex gap-2 border border-gray-200 py-2 px-3 rounded items-center hover:bg-gray-200 hover:text-black"
        style={{ transition: "all .3s" }}
      >
        <EyeIcon className="w-6" /> <span className="text-gray-600 block whitespace-nowrap">View invoice</span>
      </a>
    </div>
  )
}

export default Confirmation
